import React, { useEffect, useState, useCallback } from "react";
import {
  Check, X, ShieldCheck, UserCheck, Clock, XCircle, TrendingUp, DollarSign,
  Images, Users, RefreshCw, BarChart3, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const STATUS_LABELS = {
  pending: { label: "Pending", icon: Clock, cls: "bg-amber-100 text-amber-800" },
  approved: { label: "Approved", icon: UserCheck, cls: "bg-green-100 text-green-800" },
  rejected: { label: "Rejected", icon: XCircle, cls: "bg-red-100 text-red-800" },
};

const WINDOWS = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "all", label: "All time" },
];

const VERTICAL_COLORS = {
  software: "#0055FF", security: "#FFC700", food: "#FF8A1E",
  medical: "#14B8A6", finance: "#22C55E", beauty: "#EC4899",
  automotive: "#E63946", legal: "#1E3A8A", education: "#F59E0B",
  realestate: "#10B981", travel: "#06B6D4", home_services: "#F97316",
  generic: "#71717A",
};

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  const [usage, setUsage] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [window, setWindow] = useState("all");

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const loadUsage = useCallback(async (w) => {
    setLoadingUsage(true);
    try {
      const { data } = await api.get(`/admin/usage?window=${w}`);
      setUsage(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load usage");
    } finally {
      setLoadingUsage(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadUsage(window); }, [loadUsage, window]);

  const act = async (user, approve) => {
    try {
      await api.post(`/admin/users/${user.id}/approval`, { approve });
      toast.success(approve ? `${user.email} approved` : `${user.email} rejected`);
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const pending = users.filter((u) => u.status === "pending");
  const others = users.filter((u) => u.status !== "pending");

  const maxVerticalCost = usage?.by_vertical?.[0]?.cost_usd || 1;
  const maxUserCost = usage?.per_user?.[0]?.cost_usd || 1;

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="admin-page">
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">Administration</div>
        <h1 className="font-heading text-3xl font-bold text-zinc-900 mt-1 flex items-center gap-2">
          <ShieldCheck className="w-7 h-7 text-[#0055FF]" strokeWidth={1.75} /> Admin
        </h1>
      </div>

      {/* ============ USAGE ANALYTICS ============ */}
      <section className="mb-12" data-testid="usage-section">
        <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">API Usage</div>
            <h2 className="font-heading text-xl font-semibold text-zinc-900 mt-1 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-zinc-500" strokeWidth={1.75} /> Who's burning the Gemini budget
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-sm border border-zinc-200 overflow-hidden">
              {WINDOWS.map((w) => (
                <button
                  key={w.key}
                  onClick={() => setWindow(w.key)}
                  className={`px-3 py-1.5 text-xs font-medium border-r border-zinc-200 last:border-r-0 ${
                    window === w.key
                      ? "bg-zinc-900 text-white"
                      : "bg-white text-zinc-700 hover:bg-zinc-50"
                  }`}
                  data-testid={`window-${w.key}`}
                >
                  {w.label}
                </button>
              ))}
            </div>
            <Button
              variant="outline" size="sm"
              onClick={() => loadUsage(window)}
              className="rounded-sm"
              data-testid="refresh-usage"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingUsage ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {/* Headline stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <StatCard
            icon={DollarSign} label="Total spend"
            value={usage ? `$${usage.total_cost_usd.toFixed(2)}` : "—"}
            testid="stat-total-cost"
            loading={loadingUsage}
          />
          <StatCard
            icon={Images} label="Images generated"
            value={usage ? usage.total_images.toLocaleString() : "—"}
            testid="stat-total-images"
            loading={loadingUsage}
          />
          <StatCard
            icon={Users} label="Active users"
            value={usage ? `${usage.active_users} / ${usage.total_users}` : "—"}
            sublabel="of registered"
            testid="stat-active-users"
            loading={loadingUsage}
          />
          <StatCard
            icon={TrendingUp} label="Avg cost / user"
            value={usage && usage.active_users > 0
              ? `$${(usage.total_cost_usd / usage.active_users).toFixed(2)}`
              : "$0.00"}
            testid="stat-avg-cost"
            loading={loadingUsage}
          />
        </div>

        {/* Per-user spend ranking */}
        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden mb-6" data-testid="per-user-section">
          <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
            <h3 className="font-heading text-sm font-semibold text-zinc-900">Spend by media buyer</h3>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
              ranked by cost · {window}
            </span>
          </div>
          {loadingUsage ? (
            <div className="py-10 flex items-center justify-center text-zinc-400">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          ) : !usage?.per_user?.length ? (
            <div className="py-10 text-center text-sm text-zinc-500">
              No generations yet in this window.
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {usage.per_user.map((u, i) => {
                const pct = Math.max(2, (u.cost_usd / maxUserCost) * 100);
                return (
                  <div key={u.user_id} className="px-4 py-3" data-testid={`user-row-${u.user_id}`}>
                    <div className="flex items-center justify-between mb-1.5 gap-3">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className="font-mono text-[10px] text-zinc-400 w-6">#{i + 1}</span>
                        <div className="w-8 h-8 rounded-sm bg-zinc-100 flex items-center justify-center font-mono text-xs font-semibold text-zinc-600 flex-shrink-0">
                          {(u.name || u.email)[0]?.toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-zinc-900 truncate">{u.name}</div>
                          <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-mono text-base font-semibold text-zinc-900">
                          ${u.cost_usd.toFixed(2)}
                        </div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                          {u.images.toLocaleString()} imgs
                        </div>
                      </div>
                    </div>
                    <div className="h-1.5 bg-zinc-100 rounded-sm overflow-hidden">
                      <div
                        className="h-full bg-[#0055FF] transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Vertical breakdown */}
        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden" data-testid="vertical-section">
          <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
            <h3 className="font-heading text-sm font-semibold text-zinc-900">Spend by vertical</h3>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
              top niches · {window}
            </span>
          </div>
          {loadingUsage ? (
            <div className="py-10 flex items-center justify-center text-zinc-400">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          ) : !usage?.by_vertical?.length ? (
            <div className="py-10 text-center text-sm text-zinc-500">No data in this window.</div>
          ) : (
            <div className="p-4 space-y-3">
              {usage.by_vertical.map((v) => {
                const pct = Math.max(2, (v.cost_usd / maxVerticalCost) * 100);
                const color = VERTICAL_COLORS[v.vertical] || "#71717A";
                return (
                  <div key={v.vertical} data-testid={`vertical-row-${v.vertical}`}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                        <span className="font-medium text-zinc-800 capitalize">{v.vertical.replace("_", " ")}</span>
                      </span>
                      <span className="font-mono text-zinc-600">
                        ${v.cost_usd.toFixed(2)} · <span className="text-zinc-400">{v.images}</span>
                      </span>
                    </div>
                    <div className="h-2 bg-zinc-100 rounded-sm overflow-hidden">
                      <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ============ USER APPROVAL ============ */}
      <section data-testid="approval-section">
        <div className="mb-4">
          <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">User approval</div>
          <h2 className="font-heading text-xl font-semibold text-zinc-900 mt-1">Access requests</h2>
        </div>

        {loadingUsers ? (
          <div className="text-zinc-500">Loading...</div>
        ) : (
          <>
            <Section title="Pending requests" count={pending.length} testId="pending-section">
              {pending.length === 0 ? (
                <EmptyRow message="No pending requests." />
              ) : (
                pending.map((u) => (
                  <Row key={u.id} user={u}>
                    <Button
                      size="sm" onClick={() => act(u, true)}
                      className="rounded-sm bg-[#16A34A] hover:bg-green-700 h-8"
                      data-testid={`approve-user-${u.id}`}
                    >
                      <Check className="w-3.5 h-3.5 mr-1" /> Approve
                    </Button>
                    <Button
                      size="sm" variant="outline" onClick={() => act(u, false)}
                      className="rounded-sm h-8 border-zinc-300 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
                      data-testid={`reject-user-${u.id}`}
                    >
                      <X className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                  </Row>
                ))
              )}
            </Section>

            <Section title="All users" count={others.length} testId="all-users-section">
              {others.map((u) => (
                <Row key={u.id} user={u}>
                  {u.status === "rejected" && (
                    <Button size="sm" onClick={() => act(u, true)} className="rounded-sm bg-[#0055FF] hover:bg-[#0040CC] h-8" data-testid={`reapprove-user-${u.id}`}>
                      Re-approve
                    </Button>
                  )}
                  {u.status === "approved" && u.role !== "admin" && (
                    <Button size="sm" variant="outline" onClick={() => act(u, false)} className="rounded-sm h-8" data-testid={`revoke-user-${u.id}`}>
                      Revoke
                    </Button>
                  )}
                </Row>
              ))}
            </Section>
          </>
        )}
      </section>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sublabel, testid, loading }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-zinc-400">
        <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
        <span className="font-mono text-[10px] uppercase tracking-widest">{label}</span>
      </div>
      <div className="mt-2 font-heading text-2xl font-bold text-zinc-900 tabular-nums">
        {loading ? <span className="inline-block w-16 h-7 bg-zinc-100 rounded-sm animate-pulse" /> : value}
      </div>
      {sublabel && <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 mt-0.5">{sublabel}</div>}
    </div>
  );
}

function Section({ title, count, testId, children }) {
  return (
    <section className="mb-8" data-testid={testId}>
      <div className="flex items-baseline gap-3 mb-3">
        <h3 className="font-heading text-sm font-semibold text-zinc-900">{title}</h3>
        <span className="font-mono text-xs uppercase tracking-widest text-zinc-400">{count}</span>
      </div>
      <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white">
        <div className="divide-y divide-zinc-100">{children}</div>
      </div>
    </section>
  );
}

function Row({ user, children }) {
  const meta = STATUS_LABELS[user.status] || STATUS_LABELS.pending;
  const Icon = meta.icon;
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3" data-testid={`user-row-${user.id}`}>
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="w-9 h-9 rounded-sm bg-zinc-100 flex items-center justify-center font-mono text-sm font-semibold text-zinc-600 flex-shrink-0">
          {(user.name || user.email)[0]?.toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-zinc-900 truncate">{user.name || user.email}</div>
          <div className="text-xs text-zinc-500 truncate">{user.email}</div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.cls}`}>
          <Icon className="w-3 h-3" strokeWidth={2} /> {meta.label}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 tabular-nums">
          {user.total_images || 0} imgs · ${(user.total_cost_usd || 0).toFixed(2)}
        </span>
        {user.role === "admin" && (
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#0055FF]">admin</span>
        )}
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}

function EmptyRow({ message }) {
  return <div className="px-4 py-6 text-sm text-zinc-500 text-center">{message}</div>;
}
